// @ts-nocheck
/**
 * ドラフトシステム - ピック画面
 */

import React, { useState } from 'react';
import { useDraftPick } from '../hooks';
import { DRAFT_DIRECTION } from '../constants';
import { draftStyles, getRarityStyle } from './styles/draftStyles';
import Card from '../../components/Card';
import { useIsMobile } from '../../collection/hooks/useMediaQuery';

/**
 * ドラフトピック画面
 * @param {Object} props
 * @param {Object} props.session - ドラフトセッション
 * @param {Function} props.onPick - ピックコールバック
 * @param {Function} props.onCancel - キャンセルコールバック
 */
export const DraftPickScreen = ({
  session,
  onPick,
  onCancel,
}) => {
  const {
    currentPack,
    selectedCard,
    timeRemaining,
    roundInfo,
    remainingPicks,
    selectCard,
    confirmPick,
    hasSelection,
    packCount,
  } = useDraftPick(session, onPick);

  // モバイル判定
  const isMobile = useIsMobile();

  // ホバー中のカード（詳細表示用）
  const [hoveredCard, setHoveredCard] = useState(null);

  // ピック済みカード
  const picks = session?.draft?.picks?.player || [];

  // 方向アイコン
  const directionIcon = roundInfo.direction === DRAFT_DIRECTION.CLOCKWISE ? '→' : '←';

  // タイマーの警告状態
  const isTimerWarning = timeRemaining <= 10;

  // カードをクリック
  const handleCardClick = (card) => {
    if (selectedCard?.id === card.id) {
      // ダブルクリックでピック
      confirmPick();
    } else {
      selectCard(card);
    }
  };

  // 詳細表示するカード（選択中 > ホバー中）
  const detailCard = selectedCard || hoveredCard;

  return (
    <div style={draftStyles.pickContainer}>
      {/* ヘッダー */}
      <div style={{
        ...draftStyles.pickHeader,
        padding: isMobile ? '8px 12px' : '15px 20px',
        flexWrap: isMobile ? 'wrap' : 'nowrap',
        gap: isMobile ? '6px' : '0',
      }}>
        <div style={{
          ...draftStyles.roundInfo,
          gap: isMobile ? '8px' : '20px',
        }}>
          <span style={{
            ...draftStyles.roundText,
            fontSize: isMobile ? '0.85rem' : '1.1rem',
          }}>
            パック {roundInfo.pack} / R{roundInfo.round}
          </span>
          {!isMobile && (
            <span style={draftStyles.roundText}>
              {directionIcon} {roundInfo.direction === DRAFT_DIRECTION.CLOCKWISE ? '時計回り' : '反時計回り'}
            </span>
          )}
        </div>

        <div style={draftStyles.timerContainer}>
          <span style={{
            ...draftStyles.timerIcon,
            fontSize: isMobile ? '1rem' : '1.2rem',
          }}>⏱️</span>
          <span style={{
            ...draftStyles.timerText,
            ...(isTimerWarning ? draftStyles.timerWarning : draftStyles.timerNormal),
            fontSize: isMobile ? '1rem' : '1.2rem',
          }}>
            {timeRemaining}s
          </span>
        </div>

        <span style={{
          ...draftStyles.progressInfo,
          fontSize: isMobile ? '0.8rem' : '0.9rem',
        }}>
          残り {remainingPicks}枚
        </span>
      </div>

      {/* メインエリア */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '10px' : '20px',
        padding: isMobile ? '10px' : '20px',
        overflow: 'hidden',
      }}>
        {/* パック表示エリア */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          minHeight: isMobile ? '0' : 'auto',
        }}>
          <div style={{
            ...draftStyles.packTitle,
            fontSize: isMobile ? '0.85rem' : '1rem',
            marginBottom: isMobile ? '8px' : '15px',
          }}>
            パックから1枚選んでください（{packCount}枚）
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile
              ? 'repeat(auto-fill, minmax(70px, 1fr))'
              : 'repeat(auto-fill, minmax(100px, 1fr))',
            gap: isMobile ? '6px' : '12px',
            justifyContent: 'center',
            overflowY: 'auto',
            padding: isMobile ? '5px' : '10px',
            flex: 1,
          }}>
            {currentPack.map((card, index) => (
              <div
                key={card.id + '_' + index}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: isMobile ? '2px' : '4px',
                }}
              >
                <Card
                  card={card}
                  onClick={() => handleCardClick(card)}
                  selected={selectedCard?.id === card.id}
                  onMouseEnter={() => setHoveredCard(card)}
                  onMouseLeave={() => setHoveredCard(null)}
                  small={isMobile}
                />
                {/* レアリティバッジ */}
                <span style={{
                  ...draftStyles.cardRarity,
                  ...getRarityStyle(card.rarity),
                  fontSize: isMobile ? '0.6rem' : '0.7rem',
                  padding: isMobile ? '1px 4px' : '2px 6px',
                }}>
                  {card.rarity}
                </span>
              </div>
            ))}
          </div>

          {/* ピック済みリスト（モバイルでは非表示） */}
          {!isMobile && picks.length > 0 && (
            <div style={draftStyles.picksSection}>
              <div style={draftStyles.picksTitle}>
                ピック済み（{picks.length}枚）
              </div>
              <div style={draftStyles.picksList}>
                {picks.map((card, index) => (
                  <span key={card.id + '_' + index} style={draftStyles.picksItem}>
                    {card.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* カード詳細パネル */}
        <div style={{
          width: isMobile ? '100%' : '280px',
          maxHeight: isMobile ? '140px' : 'none',
          background: 'rgba(255, 255, 255, 0.03)',
          borderRadius: isMobile ? '8px' : '12px',
          padding: isMobile ? '10px' : '15px',
          display: 'flex',
          flexDirection: isMobile ? 'row' : 'column',
          gap: isMobile ? '10px' : '12px',
          flexShrink: 0,
          overflow: isMobile ? 'hidden' : 'visible',
        }}>
          {isMobile ? (
            // モバイル用コンパクト表示
            detailCard ? (
              <>
                {/* 左側：基本情報 */}
                <div style={{
                  flex: '0 0 auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  minWidth: '100px',
                }}>
                  <div style={{
                    fontSize: '0.9rem',
                    fontWeight: 'bold',
                    color: '#fff',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {detailCard.name}
                  </div>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '4px',
                    fontSize: '0.75rem',
                  }}>
                    <span style={{
                      ...draftStyles.cardRarity,
                      ...getRarityStyle(detailCard.rarity),
                      fontSize: '0.65rem',
                      padding: '1px 4px',
                    }}>
                      {detailCard.rarity}
                    </span>
                    <span style={{ color: '#aaa' }}>💎{detailCard.cost || 0}</span>
                    {detailCard.attribute && (
                      <span style={{ color: '#aaa' }}>{detailCard.attribute}</span>
                    )}
                  </div>
                  {detailCard.type === 'monster' && (
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                    }}>
                      <span style={{ color: '#ff6b6b' }}>⚔️{detailCard.attack}</span>
                      <span style={{ color: '#6bff6b' }}>❤️{detailCard.hp}</span>
                    </div>
                  )}
                </div>
                {/* 右側：効果テキスト */}
                <div style={{
                  flex: 1,
                  fontSize: '0.7rem',
                  color: '#ccc',
                  lineHeight: '1.4',
                  background: 'rgba(0,0,0,0.2)',
                  padding: '6px',
                  borderRadius: '4px',
                  overflowY: 'auto',
                  maxHeight: '100%',
                }}>
                  {detailCard.effect || 'なし'}
                </div>
              </>
            ) : (
              <div style={{ color: '#666', fontSize: '0.8rem', padding: '10px' }}>
                カードをタップで詳細表示
              </div>
            )
          ) : (
            // デスクトップ用フル表示
            <>
              <div style={{
                fontSize: '1rem',
                fontWeight: 'bold',
                color: '#ffd700',
                borderBottom: '1px solid #444',
                paddingBottom: '8px',
              }}>
                カード詳細
              </div>

              {detailCard ? (
                <>
                  {/* カード名 */}
                  <div style={{
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    color: '#fff',
                  }}>
                    {detailCard.name}
                  </div>

                  {/* 基本情報 */}
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px',
                    fontSize: '0.9rem',
                  }}>
                    <span style={{
                      ...draftStyles.cardRarity,
                      ...getRarityStyle(detailCard.rarity),
                    }}>
                      {detailCard.rarity}
                    </span>
                    <span style={{ color: '#aaa' }}>
                      💎 {detailCard.cost || 0}
                    </span>
                    {detailCard.attribute && (
                      <span style={{ color: '#aaa' }}>
                        {detailCard.attribute}
                      </span>
                    )}
                    <span style={{ color: '#888' }}>
                      {detailCard.type === 'monster' ? 'モンスター' :
                       detailCard.type === 'magic' ? '魔法' :
                       detailCard.type === 'field' ? 'フィールド' : 'フェイズ'}
                    </span>
                  </div>

                  {/* モンスターステータス */}
                  {detailCard.type === 'monster' && (
                    <div style={{
                      display: 'flex',
                      gap: '20px',
                      fontSize: '1rem',
                      fontWeight: 'bold',
                    }}>
                      <span style={{ color: '#ff6b6b' }}>⚔️ {detailCard.attack}</span>
                      <span style={{ color: '#6bff6b' }}>❤️ {detailCard.hp}</span>
                    </div>
                  )}

                  {/* カテゴリ */}
                  {detailCard.category && (
                    <div style={{ fontSize: '0.85rem', color: '#9d4ce6' }}>
                      {detailCard.category}
                    </div>
                  )}

                  {/* 効果テキスト */}
                  {detailCard.effect && (
                    <div style={{
                      fontSize: '0.85rem',
                      color: '#ccc',
                      lineHeight: '1.5',
                      background: 'rgba(0,0,0,0.2)',
                      padding: '10px',
                      borderRadius: '6px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                    }}>
                      {detailCard.effect}
                    </div>
                  )}

                  {/* 技情報 */}
                  {detailCard.type === 'monster' && (detailCard.basicSkill || detailCard.advancedSkill) && (
                    <div style={{
                      fontSize: '0.85rem',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px',
                    }}>
                      {detailCard.basicSkill && (
                        <div style={{ color: '#4caf50' }}>
                          <strong>基本技:</strong> {detailCard.basicSkill.text}
                        </div>
                      )}
                      {detailCard.advancedSkill && (
                        <div style={{ color: '#ff9800' }}>
                          <strong>上級技:</strong> {detailCard.advancedSkill.text}
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div style={{ color: '#666', textAlign: 'center', padding: '40px 0' }}>
                  カードにカーソルを合わせると<br />詳細が表示されます
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* アクションエリア */}
      <div style={{
        ...draftStyles.pickActionArea,
        padding: isMobile ? '10px 12px' : '20px',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '8px' : '0',
      }}>
        <div style={{
          ...draftStyles.selectedCardInfo,
          textAlign: isMobile ? 'center' : 'left',
        }}>
          {selectedCard ? (
            <>
              <div style={{
                ...draftStyles.selectedCardName,
                fontSize: isMobile ? '0.9rem' : '1.1rem',
              }}>
                選択中: {selectedCard.name}
              </div>
              <div style={{
                ...draftStyles.selectedCardDetails,
                fontSize: isMobile ? '0.8rem' : '0.9rem',
              }}>
                {selectedCard.type === 'monster' && (
                  <>⚔️{selectedCard.attack} ❤️{selectedCard.hp} 💎{selectedCard.cost || 0}</>
                )}
                {selectedCard.type !== 'monster' && (
                  <>💎{selectedCard.cost || 0}</>
                )}
              </div>
            </>
          ) : (
            <div style={{ color: '#666', fontSize: isMobile ? '0.8rem' : '1rem' }}>
              カードを選択してください
            </div>
          )}
        </div>

        <div style={{
          display: 'flex',
          gap: '10px',
          width: isMobile ? '100%' : 'auto',
        }}>
          <button
            onClick={onCancel}
            style={{
              ...draftStyles.cancelButton,
              flex: isMobile ? 1 : 'none',
              padding: isMobile ? '10px 15px' : '15px 30px',
              fontSize: isMobile ? '0.85rem' : '1rem',
            }}
          >
            中断
          </button>
          <button
            onClick={confirmPick}
            disabled={!hasSelection}
            style={{
              ...draftStyles.pickButton,
              ...(!hasSelection ? draftStyles.pickButtonDisabled : {}),
              flex: isMobile ? 2 : 'none',
              padding: isMobile ? '10px 20px' : '15px 40px',
              fontSize: isMobile ? '0.9rem' : '1.1rem',
            }}
          >
            ピック確定
          </button>
        </div>
      </div>
    </div>
  );
};

export default DraftPickScreen;
